import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { collection, addDoc, doc, updateDoc, onSnapshot, query, orderBy, limit, startAfter, getDocs, getDoc, where } from 'firebase/firestore';
import { sendNotification } from '../utils/notifications';
import { db } from '../firebase';

const BooksContext = createContext();
const BOOKS_PER_PAGE = 50;

export function BooksProvider({ children }) {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);

  // Chargement initial limité + temps réel
  useEffect(() => {
    const q = query(collection(db, 'books'), orderBy('createdAt', 'desc'), limit(BOOKS_PER_PAGE));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const booksData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setBooks(booksData);
      setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMore(snapshot.docs.length === BOOKS_PER_PAGE);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const loadMoreBooks = useCallback(async () => {
    if (!hasMore || !lastDoc) return;
    try {
      const q = query(
        collection(db, 'books'),
        orderBy('createdAt', 'desc'),
        startAfter(lastDoc),
        limit(BOOKS_PER_PAGE)
      );
      const snapshot = await getDocs(q);
      if (snapshot.empty) {
        setHasMore(false);
        return;
      }
      const newBooks = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setBooks(prev => {
        const existingIds = new Set(prev.map(b => b.id));
        const unique = newBooks.filter(b => !existingIds.has(b.id));
        return [...prev, ...unique];
      });
      setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
      setHasMore(snapshot.docs.length === BOOKS_PER_PAGE);
    } catch (e) {
      console.error('Erreur loadMoreBooks:', e);
    }
  }, [hasMore, lastDoc]);

  const addBook = async (book) => {
    const docRef = await addDoc(collection(db, 'books'), {
      ...book,
      coverImage: book.coverImage || null,
      likes: 0,
      views: 0,
      ratings: {},
      chapters: [],
      status: book.status || 'draft',
      authorNote: book.authorNote || '',
      createdAt: new Date().toISOString().split('T')[0],
    });
    return { id: docRef.id, ...book };
  };

  const publishBook = async (bookId) => {
    const bookRef = doc(db, 'books', bookId);
    await updateDoc(bookRef, { status: 'published' });

    const book = books.find(b => b.id === bookId);
    if (book) {
      try {
        const authorUid = book.authorId?.replace('author_', '');
        if (authorUid) {
          const socialSnap = await getDocs(query(collection(db, 'social'), where('following', 'array-contains', authorUid)));
          socialSnap.docs.forEach(followerDoc => {
            sendNotification({
              recipientId: followerDoc.id,
              fromId: authorUid,
              fromName: book.author,
              type: 'new_book',
              bookId,
              bookTitle: book.title,
            });
          });
        }
      } catch (e) {
        console.error('Erreur notifications publication:', e);
      }
    }
  };

  const unpublishBook = async (bookId) => {
    const bookRef = doc(db, 'books', bookId);
    await updateDoc(bookRef, { status: 'draft' });
  };

  const deleteBook = async (bookId) => {
    const bookRef = doc(db, 'books', bookId);
    const { deleteDoc } = await import('firebase/firestore');
    await deleteDoc(bookRef);
  };

  const updateBook = async (bookId, data) => {
    const bookRef = doc(db, 'books', bookId);
    await updateDoc(bookRef, data);
  };

  const addChapter = async (bookId, chapter) => {
    const bookRef = doc(db, 'books', bookId);
    const book = books.find(b => b.id === bookId);
    if (!book) return;
    const newChapters = [...book.chapters, { ...chapter, id: 'ch' + (book.chapters.length + 1) }];
    await updateDoc(bookRef, { chapters: newChapters });
  };

  const likeBook = async (bookId) => {
    const bookRef = doc(db, 'books', bookId);
    const book = books.find(b => b.id === bookId);
    if (!book) return;
    await updateDoc(bookRef, { likes: book.likes + 1 });
  };

  const getBookById = (bookId) => books.find(b => b.id === bookId);
  const getBooksByAuthor = (authorId) => books.filter(b => b.authorId === authorId);

  const incrementViews = async (bookId) => {
    const bookRef = doc(db, 'books', bookId);
    const book = books.find(b => b.id === bookId);
    if (!book) return;
    await updateDoc(bookRef, { views: (book.views || 0) + 1 });
  };

  return (
    <BooksContext.Provider value={{ books, loading, addBook, addChapter, likeBook, getBookById, getBooksByAuthor, publishBook, unpublishBook, deleteBook, updateBook, incrementViews, loadMoreBooks, hasMore }}>
      {children}
    </BooksContext.Provider>
  );
}

export function useBooks() {
  return useContext(BooksContext);
}